/**
 * interior_shell_batch.ts — THE SHELL AS A HANDFUL OF DRAWS (the interiors programme, slice H, 2026-10-02).
 *
 * MEASURED (impl-polish.md): an interior's shell was one mesh, one geometry and one draw per box and per window pane —
 * 496 boxes + 206 panes in an 8-storey tower: 86 % of its draws and 90 % of its geometries, for 6,364 triangles. A
 * city frame is bound by per-object CPU work, and the visit adds its shell on top of the city it stands in.
 *
 * THE PICTURE DOES NOT CHANGE. Every box is the same `BoxGeometry` with the same world-scale uv rule, translated to
 * its building-local centre and merged with the others ON ITS MATERIAL AND STOREY; every pane is one instance of a unit
 * plane (its wall's quarter turn and its own width × height in the instance matrix) in its storey's batch on the
 * visit's one glass material. ONE BATCH PER STOREY: three's frustum test still drops a storey off screen, a storey can
 * be hidden whole (a top-down picture, a later storey rule), and a batch is never taller than a floor. The
 * batches hang under ONE root seated at the building's own origin and yaw, so the vertices are building-local metres:
 * float32 holds them exactly 500 km from the world origin (merged in absolute coordinates they would snap by 3 cm).
 *
 * WHAT EVERY READER STILL FINDS: the root is a child of the visit's `building-interior` group (the area lights walk
 * up to its `userData.interiorId`; they already read an instanced glass batch per instance); the glass material is
 * the visit's own NAMED one; every opaque batch is on the rain-shelter layer (THE ROOF-INDOORS LAW); nothing is
 * tagged `sharedAsset`, so the visit's teardown frees the batches' geometry with the rest of the shell.
 *
 * Lever: `atlas:interior-merge = 'off'` (interior_wiring.ts) → the visit builds one mesh per box and pane, as before.
 * MEASURED (impl-polish.md): tower 944 → 247 draws, 780 → 83 geometries, the door picture's mean within 0.6 of 255.
 */
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { FLOOR_H, type IntBox, type InteriorSpec } from '../../lib/building_interiors.js';
import { RAIN_SHELTER_LAYER } from '../../lib/rain_shelter.js';
import type { ShellMaterials } from './interior_look.js';

export const SHELL_ROOT_NAME = 'interior-shell';
export const SHELL_PANES_NAME = 'interior-panes';

export interface ShellBatchReport { boxes: number; panes: number; meshes: number; triangles: number }

/** One box's geometry in building-local metres, with the visit's world-scale uv rule (~2.5 m per texture tile). */
export function shellBoxGeometry(box: IntBox, local: boolean): THREE.BoxGeometry {
  const geo = new THREE.BoxGeometry(box.w, box.h, box.d);
  if (box.mat === 'floor' || box.mat === 'wall' || box.mat === 'stair') {
    const su = Math.max(box.w, box.d) / 2.5;
    const sv = (box.mat === 'floor' ? Math.min(box.w, box.d) : Math.max(box.h, 0.4)) / 2.5;
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
    uv.needsUpdate = true;
  }
  if (local) geo.translate(box.x, box.y, box.z);
  return geo;
}

/**
 * The whole drawn shell as batches under one root at the building's origin. `at` = where the building stands
 * (absolute) and how it is turned — the visit's own `toWorld` frame (`rotation.y = yaw`).
 */
export function buildShellBatches(
  spec: InteriorSpec, mats: ShellMaterials, at: { x: number; groundY: number; z: number; yaw: number },
): { root: THREE.Group; report: ShellBatchReport } {
  const root = new THREE.Group();
  root.name = SHELL_ROOT_NAME;
  root.position.set(at.x, at.groundY, at.z);
  root.rotation.y = at.yaw;
  const report: ShellBatchReport = { boxes: 0, panes: spec.panes.length, meshes: 0, triangles: 0 };
  const storeyOf = (y: number): number => Math.max(0, Math.min(Math.max(1, spec.floors), Math.floor((y + 0.01) / FLOOR_H)));
  const byMat = new Map<string, { mat: IntBox['mat']; floor: number; parts: THREE.BufferGeometry[] }>();
  for (const box of spec.boxes) {
    if (box.render === false) continue;
    report.boxes++;
    const floor = storeyOf(box.y);
    const k = `${box.mat}|${floor}`;
    const geo = shellBoxGeometry(box, true);
    const e = byMat.get(k);
    if (e) e.parts.push(geo); else byMat.set(k, { mat: box.mat, floor, parts: [geo] });
  }
  for (const { mat, floor, parts } of byMat.values()) {
    // one part needs no merge; a failed merge (it cannot fail: every part is a BoxGeometry) keeps the parts as meshes
    const merged = parts.length === 1 ? parts[0] : mergeGeometries(parts, false);
    const geos = merged ? [merged] : parts;
    if (merged && parts.length > 1) for (const p of parts) p.dispose();
    for (const geo of geos) {
      const mesh = new THREE.Mesh(geo, mats[mat]);
      mesh.name = `${SHELL_ROOT_NAME}:${mat}:f${floor}`;
      mesh.userData.shellFloor = floor;
      mesh.layers.enable(RAIN_SHELTER_LAYER); // THE ROOF-INDOORS LAW: the shell is the roof the rain map sees while the exterior hides
      mesh.matrixAutoUpdate = false; // identity under the root, for the whole visit
      root.add(mesh);
      report.meshes++;
      report.triangles += (geo.index ? geo.index.count : geo.attributes.position.count) / 3;
    }
  }
  const paneFloors = new Map<number, typeof spec.panes>();
  for (const pane of spec.panes) { const f = storeyOf(pane.y); const l = paneFloors.get(f); if (l) l.push(pane); else paneFloors.set(f, [pane]); }
  const unit = paneFloors.size > 0 ? new THREE.PlaneGeometry(1, 1) : null; // one unit plane for every storey's batch
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), sc = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  for (const [floor, list] of paneFloors) {
    const panes = new THREE.InstancedMesh(unit!, mats.glass, list.length);
    panes.name = `${SHELL_PANES_NAME}:f${floor}`;
    panes.userData.shellFloor = floor;
    list.forEach((pane, i) => {
      // a pane faces its wall's normal: x-axis walls face ±X (a quarter turn), z-axis walls ±Z
      q.setFromAxisAngle(up, pane.axis === 'x' ? Math.PI / 2 : 0);
      panes.setMatrixAt(i, m.compose(p.set(pane.x, pane.y, pane.z), q, sc.set(pane.w, pane.h, 1)));
    });
    panes.instanceMatrix.needsUpdate = true;
    panes.computeBoundingSphere();
    panes.matrixAutoUpdate = false;
    root.add(panes);
    report.meshes++;
    report.triangles += list.length * 2;
  }
  root.updateMatrix();
  return { root, report };
}
